import React, { useState, useCallback, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { heroWsbDiverKelpDark } from '../assets/images/hero/imageAssets';
import SEOHead from '../components/SEOHead';
import LightboxModal from '../components/LightboxModal';
import { useInstagramFeed } from '../hooks/useInstagramFeed';
import { getOgImageUrl, OG_IMAGE_DIMENSIONS } from '../utils/seoSchemas';
import type { GalleryImage as GalleryImageType } from '../hooks/useInstagramFeed';
import {
  HeroSection,
  HeroContent,
  HeroEyebrow,
  HeroTitle,
  HeroSubtitle,
  FilterSection,
  FilterContainer,
  FilterButton,
  GridSection,
  GridContainer,
  MasonryGrid,
  GalleryItem,
  GalleryImage,
  ImageOverlay,
  OverlayCaption,
  LiveBadge,
  LoadingContainer,
  LoadingDot,
  EmptyState,
  InstagramCTA,
  CTAContainer,
  CTAHandle,
  CTATitle,
  CTAText,
  CTAButton,
  LiveFeedBadge,
  PulseDot,
} from './styles/GalleryPage.styles';

/* ─── Types ─── */

type FilterCategory = 'all' | 'underwater' | 'catch' | 'vessel' | 'crew';

interface FilterOption {
  key: FilterCategory;
  label: string;
}

const FILTERS: FilterOption[] = [
  { key: 'all', label: 'All' },
  { key: 'underwater', label: 'Underwater' },
  { key: 'catch', label: 'The Catch' },
  { key: 'vessel', label: 'Vessel' },
  { key: 'crew', label: 'Crew' },
];

/* ─── Animated Section Wrapper ─── */

const AnimatedSection = ({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.6, delay, ease: 'easeOut' }}
    >
      {children}
    </motion.div>
  );
};

/* ─── Gallery Page ─── */

const GalleryPage = () => {
  const { images, loading, isLive } = useInstagramFeed();
  const [activeFilter, setActiveFilter] = useState<FilterCategory>('all');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const filteredImages = activeFilter === 'all'
    ? images
    : images.filter((img) => img.category === activeFilter);

  const handleImageClick = useCallback((image: GalleryImageType) => {
    const idx = filteredImages.indexOf(image);
    setLightboxIndex(idx >= 0 ? idx : 0);
    setLightboxOpen(true);
  }, [filteredImages]);

  const handleCloseLightbox = useCallback(() => {
    setLightboxOpen(false);
  }, []);

  const handleNavigate = useCallback((index: number) => {
    setLightboxIndex(index);
  }, []);

  return (
    <>
      <SEOHead
        title="Gallery — Fussaki Spearfishing Charters | San Pedro, CA"
        description="Browse real catches, underwater shots, and trip photos from Fussaki Spearfishing Charters. Bluefin Tuna, White Seabass, Yellowtail, and more from Southern California waters."
        canonical="https://fussaki.com/gallery"
        ogImage={getOgImageUrl(heroWsbDiverKelpDark)}
        ogImageWidth={OG_IMAGE_DIMENSIONS.heroWsbDiverKelpDark.width}
        ogImageHeight={OG_IMAGE_DIMENSIONS.heroWsbDiverKelpDark.height}
      />

      {/* ─── Hero ─── */}
      <HeroSection $bgImage={heroWsbDiverKelpDark} role="img" aria-label="Spearfisher with White Seabass swimming through dark kelp forest">
        <HeroContent>
          <HeroEyebrow>Gallery</HeroEyebrow>
          <HeroTitle>From <span>the Water</span></HeroTitle>
          <HeroSubtitle>
            Real catches. Real trips. Real Southern California spearfishing.
          </HeroSubtitle>
        </HeroContent>
      </HeroSection>

      {/* ─── Filter Bar ─── */}
      <FilterSection>
        <FilterContainer>
          {FILTERS.map((filter) => (
            <FilterButton
              key={filter.key}
              $active={activeFilter === filter.key}
              onClick={() => setActiveFilter(filter.key)}
            >
              {filter.label}
            </FilterButton>
          ))}
        </FilterContainer>
      </FilterSection>

      {/* ─── Masonry Grid ─── */}
      <GridSection>
        <GridContainer>
          {loading ? (
            <LoadingContainer>
              <LoadingDot $delay={0} />
              <LoadingDot $delay={0.15} />
              <LoadingDot $delay={0.3} />
            </LoadingContainer>
          ) : filteredImages.length === 0 ? (
            <EmptyState>No images in this category yet.</EmptyState>
          ) : (
            <AnimatePresence mode="wait">
              <motion.div
                key={activeFilter}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <MasonryGrid>
                  {filteredImages.map((image, index) => (
                    <GalleryItemCard
                      key={image.id}
                      image={image}
                      index={index}
                      isLive={isLive}
                      onClick={handleImageClick}
                    />
                  ))}
                </MasonryGrid>
              </motion.div>
            </AnimatePresence>
          )}
        </GridContainer>
      </GridSection>

      {/* ─── Instagram CTA ─── */}
      <InstagramCTA>
        <AnimatedSection>
          <CTAContainer>
            {isLive && (
              <LiveFeedBadge>
                <PulseDot /> Showing live from Instagram
              </LiveFeedBadge>
            )}
            <CTAHandle>@fussaki</CTAHandle>
            <CTATitle>Follow Us on Instagram</CTATitle>
            <CTAText>
              {isLive
                ? 'See the latest catches and trip updates as they happen.'
                : 'Follow us for more catches, trip updates, and behind-the-scenes content.'}
            </CTAText>
            <CTAButton
              href="https://www.instagram.com/fussaki/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <InstagramIcon />
              Follow @fussaki
            </CTAButton>
          </CTAContainer>
        </AnimatedSection>
      </InstagramCTA>

      {/* ─── Lightbox ─── */}
      <LightboxModal
        images={filteredImages}
        currentIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={handleCloseLightbox}
        onNavigate={handleNavigate}
      />
    </>
  );
};

export default GalleryPage;

/* ═══════════════════════════════════════════
   SUB-COMPONENTS
   ═══════════════════════════════════════════ */

/** Individual gallery card with lazy-loaded image + hover overlay */
const GalleryItemCard = ({ image, index, isLive, onClick }: {
  image: GalleryImageType;
  index: number;
  isLive: boolean;
  onClick: (image: GalleryImageType) => void;
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-30px' });

  return (
    <GalleryItem
      ref={ref}
      $delay={Math.min(index * 0.06, 0.6)}
      onClick={() => onClick(image)}
      style={{ opacity: isInView ? undefined : 0 }}
    >
      <GalleryImage
        src={image.src}
        alt={image.alt}
        loading="lazy"
      />
      <ImageOverlay>
        {image.caption && <OverlayCaption>{image.caption}</OverlayCaption>}
        {isLive && image.permalink && (
          <LiveBadge>
            <InstagramIcon size={10} /> IG
          </LiveBadge>
        )}
      </ImageOverlay>
    </GalleryItem>
  );
};

/** Inline Instagram SVG icon */
const InstagramIcon = ({ size = 16 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
  </svg>
);
